import { type CSSProperties } from 'react'
import { Link } from 'react-router'
import {
	capKicksInAt,
	type FestivalPricingInput,
	getPriceSummary,
	PAY_WHAT_YOU_CAN_TYPES,
	type PriceStep,
	TICKET_TYPE_LABEL,
} from '#app/domains/pricing/pure'
import { type ProgramEvent } from '#app/domains/program/pure'
import { useRevealOnce } from '#app/utils/motion.ts'

const kr = (amount: number) =>
	amount === 0 ? 'Gratis' : `${amount.toLocaleString('nb-NO')} kr`

/**
 * Tickets: what it costs (drawn from the festival's own pricing, never a
 * guessed price) and how buying works, in three illustrated steps.
 */
export function TicketInfo({
	festival,
	events,
	vippsOnline = true,
}: {
	festival: FestivalPricingInput | null
	events: ProgramEvent[]
	/** Vipps payment on the site is live; until then everyone pays at the door */
	vippsOnline?: boolean
}) {
	const { summary, childrenFree } = getPriceSummary(festival, events)

	return (
		<section
			id="billetter"
			className="hk-section hk-tickets"
			aria-labelledby="tickets-title"
		>
			<div className="hk-container">
				<header className="hk-section-head">
					<p className="hk-kicker">Billettar</p>
					<h2 id="tickets-title" className="hk-h2">
						Slik får du plass
					</h2>
					<p className="hk-section-lede">
						Plassane er få — det er trass alt nokon si stove. Reserver på
						førehand, så står stolen klar.
					</p>
				</header>

				<div className="hk-tickets-grid">
					<div className="hk-price-card">
						<h3 className="hk-card-title">Pris</h3>
						{summary.kind === 'ladder' ? (
							<PriceLadder
								steps={summary.steps}
								cap={summary.cap}
								openEnded={festival?.pricingModel !== 'SIMPLE'}
							/>
						) : summary.kind === 'perConcert' ? (
							<div className="hk-price-per">
								<p>
									Kvar konsert har sin eigen pris — sjå konsertane i programmet.
									{PAY_WHAT_YOU_CAN_TYPES.filter((type) => summary[type])
										.length > 1
										? ' Prisen er etter evne: vel sjølv den som passar for deg.'
										: null}
								</p>
								<ul className="hk-price-chips">
									{PAY_WHAT_YOU_CAN_TYPES.map((type) => {
										const range = summary[type]
										if (!range) return null
										return (
											<li key={type}>
												{TICKET_TYPE_LABEL[type]}{' '}
												<strong>
													{range.from === range.to
														? kr(range.from)
														: `${kr(range.from)}–${kr(range.to)}`}
												</strong>
											</li>
										)
									})}
								</ul>
							</div>
						) : (
							<p className="hk-price-tba">
								Prisane blir lagde ut saman med billettane. Følg med!
							</p>
						)}
						{childrenFree ? (
							<p className="hk-kids">
								<span className="hk-kids-badge" aria-hidden="true">
									0 kr
								</span>
								<span>
									<strong>Barn fram til 12 år går gratis</strong> — men treng òg
									ein reservert plass.
								</span>
							</p>
						) : null}
					</div>

					<ol className="hk-steps">
						<Step
							index={0}
							title="Vel konsert"
							icon={<TicketIcon />}
							text="Trykk på ein konsert i programmet og reserver plass til alle som skal vere med."
						/>
						<Step
							index={1}
							title={
								vippsOnline ? 'Betal med Vipps — eller i døra' : 'Betal i døra'
							}
							icon={<PhoneIcon />}
							text={
								vippsOnline
									? 'Betal med Vipps med ein gong, eller med Vipps eller kontant i døra før konserten startar.'
									: 'Betal med Vipps eller kontant i døra før konserten startar. Snart kan du òg betale med Vipps her på nettsida.'
							}
						/>
						<Step
							index={2}
							title="Kom inn og finn ein stol"
							icon={<DoorIcon />}
							text="Vis billetten når du kjem. Kan du ikkje kome likevel? Gi beskjed, så får du pengane tilbake."
						/>
					</ol>
				</div>
				<p className="hk-terms">
					Les heile <Link to="/tos">vilkåra for kjøp</Link>.
				</p>
			</div>
		</section>
	)
}

function PriceLadder({
	steps,
	cap,
	openEnded,
}: {
	steps: PriceStep[]
	cap: number | null
	/** TIERED: the last tier covers "that many or more" */
	openEnded: boolean
}) {
	const [ref, reveal] = useRevealOnce<HTMLElement>({ threshold: 0.35 })
	const max = Math.max(...steps.map((step) => step.total))
	const cheapest = steps.at(-1)!
	const kicksIn = cap ? capKicksInAt(steps[0]!.total, cap) : null
	return (
		<figure ref={ref} className="pl" data-reveal={reveal}>
			<figcaption className="pl-caption">
				Pris per person etter talet på konsertar
				{cheapest.perConcert < steps[0]!.perConcert
					? ` — jo fleire, jo billegare: ned til ${kr(cheapest.perConcert)} per konsert.`
					: '.'}
			</figcaption>
			<ol className="pl-bars">
				{steps.map((step, i) => {
					const last = i === steps.length - 1
					return (
						<li
							key={step.concerts}
							className="pl-step"
							style={
								{
									'--pl-h': step.total / max,
									'--pl-i': i,
								} as CSSProperties
							}
						>
							<span className="pl-bar">
								<span className="pl-fill">
									<span className="pl-total">{kr(step.total)}</span>
								</span>
							</span>
							<span className="pl-count">
								{step.concerts}
								{last && openEnded ? '+' : ''}{' '}
								<span className="pl-count-word">
									{step.concerts === 1 ? 'konsert' : 'konsertar'}
								</span>
							</span>
							<span className="pl-per">{kr(step.perConcert)}/stk</span>
						</li>
					)
				})}
			</ol>
			{cap ? (
				<p className="pl-cap">
					{kicksIn
						? `Frå ${kicksIn} konsertar og oppover `
						: 'Uansett kor mange konsertar du går på, '}
					betaler du aldri meir enn <strong>{kr(cap)}</strong> per person.
				</p>
			) : null}
		</figure>
	)
}

function Step({
	index,
	title,
	text,
	icon,
}: {
	index: number
	title: string
	text: string
	icon: React.ReactNode
}) {
	const [ref, reveal] = useRevealOnce<HTMLLIElement>({ threshold: 0.5 })
	return (
		<li
			ref={ref}
			className="hk-step"
			data-reveal={reveal}
			style={{ '--hk-step-i': index } as CSSProperties}
		>
			<span className="hk-step-icon" aria-hidden="true">
				{icon}
			</span>
			<div>
				<h3 className="hk-step-title">
					<span className="hk-step-number">{index + 1}</span>
					{title}
				</h3>
				<p className="hk-step-text">{text}</p>
			</div>
		</li>
	)
}

function TicketIcon() {
	return (
		<svg viewBox="0 0 48 48" className="hk-icon hk-icon-ticket">
			<path
				className="hk-icon-line"
				d="M8 15a3 3 0 0 1 3-3h26a3 3 0 0 1 3 3v4a5 5 0 0 0 0 10v4a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3v-4a5 5 0 0 0 0-10z"
			/>
			<path className="hk-icon-perf" d="M30 13v22" />
			<path className="hk-icon-accent" d="M15 21h9M15 27h6" />
		</svg>
	)
}

function PhoneIcon() {
	return (
		<svg viewBox="0 0 48 48" className="hk-icon hk-icon-phone">
			<rect
				className="hk-icon-line"
				x="14"
				y="6"
				width="20"
				height="36"
				rx="4"
			/>
			<path className="hk-icon-line" d="M21 36h6" />
			<path className="hk-icon-check" d="m18.5 22 4 4 7-8" />
		</svg>
	)
}

function DoorIcon() {
	return (
		<svg viewBox="0 0 48 48" className="hk-icon hk-icon-door">
			<path className="hk-icon-glow" d="M16 9h16v31H16z" />
			<path className="hk-icon-line" d="M12 41h24M16 41V9h16v32" />
			<g className="hk-icon-leaf">
				<path className="hk-icon-leaf-body" d="M16 9h16v32H16z" />
				<circle className="hk-icon-knob" cx="28.5" cy="26" r="1.4" />
			</g>
		</svg>
	)
}
